import { catalogv1 as api } from '@/adapters';
import { extractAccount } from '@/adapters/shared/catalog/extract-account';
import { extractAccountExtension } from '@/adapters/shared/catalog/extract-account-extension';
import { extractAccountLicense } from '@/adapters/shared/catalog/extract-account-license';
import { extractAccountManager } from '@/adapters/shared/catalog/extract-account-manager';
import { extractAccountMultiFactor } from '@/adapters/shared/catalog/extract-account-multi-factor';
import { extractAccountSecret } from '@/adapters/shared/catalog/extract-account-secret';
import { extractAccountPermission } from '@/adapters/shared/catalog/extract-account-permission';
import { extractAccountPolicy } from '@/adapters/shared/catalog/extract-account-policy';
import { extractAccountRole } from '@/adapters/shared/catalog/extract-account-role';
import { extractResourceExtension } from '@/adapters/shared/catalog/extract-resource-extension';
import { extractRoleExtension } from '@/adapters/shared/catalog/extract-role-extension';
import { extractAgent } from '@/adapters/shared/catalog/extract-agent';
import { extractAgentAccount } from '@/adapters/shared/catalog/extract-agent-account';
import { extractAgentCapability } from '@/adapters/shared/catalog/extract-agent-capability';
import { extractAgentDeployment } from '@/adapters/shared/catalog/extract-agent-deployment';
import { extractAgentDeploymentAttribute } from '@/adapters/shared/catalog/extract-agent-deployment-attribute';
import { extractAgentDeploymentExtension } from '@/adapters/shared/catalog/extract-agent-deployment-extension';
import { extractAgentDeploymentResource } from '@/adapters/shared/catalog/extract-agent-deployment-resource';
import { extractAgentOwner } from '@/adapters/shared/catalog/extract-agent-owner';
import { extractApplication } from '@/adapters/shared/catalog/extract-application';
import { extractApplicationAgent } from '@/adapters/shared/catalog/extract-application-agent';
import { extractApplicationAccount } from '@/adapters/shared/catalog/extract-application-account';
import { extractApplicationGroup } from '@/adapters/shared/catalog/extract-application-group';
import { extractApplicationPermission } from '@/adapters/shared/catalog/extract-application-permission';
import { extractApplicationRole } from '@/adapters/shared/catalog/extract-application-role';
import { extractAttributes } from '@/adapters/shared/catalog/extract-attributes';
import { extractChannel } from './extract-channel';
import { extractChannelAccount } from './extract-channel-account';
import { extractComplianceBaseline } from '@/adapters/shared/catalog/extract-compliance-baseline';
import { extractContact } from '@/adapters/shared/catalog/extract-contact';
import { extractDevice } from '@/adapters/shared/catalog/extract-device';
import { extractDeviceAccount } from '@/adapters/shared/catalog/extract-device-account';
import { extractDeviceApplication } from '@/adapters/shared/catalog/extract-device-application';
import { extractEmployee } from '@/adapters/shared/catalog/extract-employee';
import { extractFederationService } from '@/adapters/shared/catalog/extract-federation-service';
import { extractFederationServiceProvider } from '@/adapters/shared/catalog/extract-federation-service-provider';
import { extractGroup } from '@/adapters/shared/catalog/extract-group';
import { extractGroupExtension } from '@/adapters/shared/catalog/extract-group-extension';
import { extractGroupLicense } from '@/adapters/shared/catalog/extract-group-license';
import { extractGroupMember } from '@/adapters/shared/catalog/extract-group-member';
import { extractGroupPermission } from '@/adapters/shared/catalog/extract-group-permission';
import { extractGroupRole } from '@/adapters/shared/catalog/extract-group-role';
import { extractLicense } from '@/adapters/shared/catalog/extract-license';
import { extractLocation } from '@/adapters/shared/catalog/extract-location';
import { extractMultiFactor } from '@/adapters/shared/catalog/extract-multi-factor';
import { extractNetworkObject } from '@/adapters/shared/catalog/extract-network-object';
import { extractOrganizationalUnit } from '@/adapters/shared/catalog/extract-organizational-unit';
import { extractPasswordPolicy } from '@/adapters/shared/catalog/extract-password-policy';
import { extractPermission } from '@/adapters/shared/catalog/extract-permission';
import { extractResource } from '@/adapters/shared/catalog/extract-resource';
import { extractResourcePermission } from '@/adapters/shared/catalog/extract-resource-permission';
import { extractPeopleExtension } from '@/adapters/shared/catalog/extract-people-extension';
import { extractPerson } from '@/adapters/shared/catalog/extract-person';
import { extractPolicy } from '@/adapters/shared/catalog/extract-policy';
import { extractRole } from '@/adapters/shared/catalog/extract-role';
import { extractRolePermission } from '@/adapters/shared/catalog/extract-role-permission';
import { extractSecret } from '@/adapters/shared/catalog/extract-secret';
import { extractSecretResource } from '@/adapters/shared/catalog/extract-secret-resource';
import { extractSecurityConfiguration } from '@/adapters/shared/catalog/extract-security-configuration';
import { extractSpecialPrincipal } from '@/adapters/shared/catalog/extract-special-principal';
import { extractTicket } from '@/adapters/shared/catalog/extract-ticket';
import { extractTicketComment } from '@/adapters/shared/catalog/extract-ticket-comment';
import { extractClassification } from '@/adapters/shared/catalog/extract-classification';
import { extractRiskFactor } from '@/adapters/shared/catalog/extract-risk-factor';
import { extractAccountAttribute } from '@/adapters/shared/catalog/extract-account-attribute';
import { extractAccountClassification } from '@/adapters/shared/catalog/extract-account-classification';
import { extractAccountRiskFactor } from '@/adapters/shared/catalog/extract-account-risk-factor';
import { extractGroupAttribute } from '@/adapters/shared/catalog/extract-group-attribute';
import { extractPersonAccount } from '@/adapters/shared/catalog/extract-person-account';
import { extractPersonAttribute } from '@/adapters/shared/catalog/extract-person-attribute';
import { extractResourceContainer } from '@/adapters/shared/catalog/extract-resource-container';
import { extractResourceContainerResource } from '@/adapters/shared/catalog/extract-resource-container-resource';
import { extractResourceContainerResourceContainer } from '@/adapters/shared/catalog/extract-resource-container-resource-container';
import { extractScopedAssignment } from '@/adapters/shared/catalog/extract-scoped-assignment';
import { extractScopedDenial } from '@/adapters/shared/catalog/extract-scoped-denial';
import { extractServiceAccountImpersonation } from '@/adapters/shared/catalog/extract-service-account-impersonation';
import { extractGroupMembershipRole } from '@/adapters/shared/catalog/extract-group-membership-role';
import { extractRoleAssignmentType } from '@/adapters/shared/catalog/extract-role-assignment-type';
import { extractOrganizationalUnitMember } from '@/adapters/shared/catalog/extract-organizational-unit-member';
import type {
  CatalogEntityId,
  CatalogEntityTypeMap,
} from '@/types/data/catalog';
import type { PageRequest } from '@/types/shared/page';
import { toApiQuery } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

export type RawEntityModel = Record<string, unknown>;

type CatalogListApiFn = (
  tenantId: UUID,
  connectorId: UUID,
  query?: ReturnType<typeof toApiQuery>
) => Promise<{ data?: unknown; ok?: boolean; status?: number }>;

export const CATALOG_ENTITY_DEFINITIONS = {
  account: { label: 'Account', apiMethod: 'listAccounts', slug: 'accounts' },
  resourceExtension: {
    label: 'Resource Extension',
    apiMethod: 'listResourceExtensions',
    slug: 'resource_extensions',
  },
  roleExtension: {
    label: 'Role Extension',
    apiMethod: 'listRoleExtensions',
    slug: 'role_extensions',
  },
  agent: { label: 'Agent', apiMethod: 'listAgents', slug: 'agents' },
  agentAccount: {
    label: 'Agent Account',
    apiMethod: 'listAgentAccounts',
    slug: 'agent_accounts',
  },
  agentCapability: {
    label: 'Agent Capability',
    apiMethod: 'listAgentCapabilities',
    slug: 'agent_capabilities',
  },
  agentDeployment: {
    label: 'Agent Deployment',
    apiMethod: 'listAgentDeployments',
    slug: 'agent_deployments',
  },
  agentDeploymentAttribute: {
    label: 'Agent Deployment Attribute',
    apiMethod: 'listAgentDeploymentAttributes',
    slug: 'agent_deployment_attributes',
  },
  agentDeploymentExtension: {
    label: 'Agent Deployment Extension',
    apiMethod: 'listAgentDeploymentExtensions',
    slug: 'agent_deployment_extensions',
  },
  agentDeploymentResource: {
    label: 'Agent Deployment Resource',
    apiMethod: 'listAgentDeploymentResources',
    slug: 'agent_deployment_resources',
  },
  agentOwner: {
    label: 'Agent Owner',
    apiMethod: 'listAgentOwners',
    slug: 'agent_owners',
  },
  applicationAgent: {
    label: 'Application Agent',
    apiMethod: 'listApplicationAgents',
    slug: 'application_agents',
  },
  accountLicense: {
    label: 'Account License',
    apiMethod: 'listAccountLicenses',
    slug: 'account_licenses',
  },
  accountManager: {
    label: 'Account Manager',
    apiMethod: 'listAccountManagers',
    slug: 'account_managers',
  },
  accountMultiFactor: {
    label: 'Account Multi-Factor',
    apiMethod: 'listAccountMultiFactors',
    slug: 'account_multi_factors',
  },
  accountSecret: {
    label: 'Account Secret',
    apiMethod: 'listAccountSecrets',
    slug: 'account_secrets',
  },
  accountPermission: {
    label: 'Account Permission',
    apiMethod: 'listAccountPermissions',
    slug: 'account_permissions',
  },
  accountPolicy: {
    label: 'Account Policy',
    apiMethod: 'listAccountPolicies',
    slug: 'account_policies',
  },
  accountRole: {
    label: 'Account Role',
    apiMethod: 'listAccountRoles',
    slug: 'account_roles',
  },
  application: {
    label: 'Application',
    apiMethod: 'listApplications',
    slug: 'applications',
  },
  applicationAccount: {
    label: 'Application Account',
    apiMethod: 'listApplicationAccounts',
    slug: 'application_accounts',
  },
  applicationGroup: {
    label: 'Application Group',
    apiMethod: 'listApplicationGroups',
    slug: 'application_groups',
  },
  applicationPermission: {
    label: 'Application Permission',
    apiMethod: 'listApplicationPermissions',
    slug: 'application_permissions',
  },
  applicationRole: {
    label: 'Application Role',
    apiMethod: 'listApplicationRoles',
    slug: 'application_roles',
  },
  attributes: {
    label: 'Attributes',
    apiMethod: 'listAttributes',
    slug: 'attributes',
  },
  classification: {
    label: 'Classification',
    apiMethod: 'listClassifications',
    slug: 'classifications',
  },
  riskFactor: {
    label: 'Risk Factor',
    apiMethod: 'listRiskFactors',
    slug: 'risk_factors',
  },
  accountAttribute: {
    label: 'Account Attribute',
    apiMethod: 'listAccountAttributes',
    slug: 'account_attributes',
  },
  accountClassification: {
    label: 'Account Classification',
    apiMethod: 'listAccountClassifications',
    slug: 'account_classifications',
  },
  accountRiskFactor: {
    label: 'Account Risk Factor',
    apiMethod: 'listAccountRiskFactors',
    slug: 'account_risk_factors',
  },
  accountExtension: {
    label: 'Account Extension',
    apiMethod: 'listAccountExtensions',
    slug: 'account_extensions',
  },
  groupAttribute: {
    label: 'Group Attribute',
    apiMethod: 'listGroupAttributes',
    slug: 'group_attributes',
  },
  groupExtension: {
    label: 'Group Extension',
    apiMethod: 'listGroupExtensions',
    slug: 'group_extensions',
  },
  personAccount: {
    label: 'Person Account',
    apiMethod: 'listPersonAccounts',
    slug: 'person_accounts',
  },
  personAttribute: {
    label: 'Person Attribute',
    apiMethod: 'listPersonAttributes',
    slug: 'person_attributes',
  },
  peopleExtension: {
    label: 'People Extension',
    apiMethod: 'listPeopleExtensions',
    slug: 'people_extensions',
  },
  resourceContainer: {
    label: 'Resource Container',
    apiMethod: 'listResourceContainers',
    slug: 'resource_containers',
  },
  resourceContainerResource: {
    label: 'Resource Container Resource',
    apiMethod: 'listResourceContainerResources',
    slug: 'resource_container_resources',
  },
  resourceContainerResourceContainer: {
    label: 'Resource Container Nesting',
    apiMethod: 'listResourceContainerResourceContainers',
    slug: 'resource_container_resource_containers',
  },
  scopedAssignment: {
    label: 'Scoped Assignment',
    apiMethod: 'listScopedAssignments',
    slug: 'scoped_assignments',
  },
  scopedDenial: {
    label: 'Scoped Denial',
    apiMethod: 'listScopedDenials',
    slug: 'scoped_denials',
  },
  serviceAccountImpersonation: {
    label: 'Service Account Impersonation',
    apiMethod: 'listServiceAccountImpersonations',
    slug: 'service_account_impersonations',
  },
  groupMembershipRole: {
    label: 'Group Membership Role',
    apiMethod: 'listGroupMembershipRoles',
    slug: 'group_membership_roles',
  },
  roleAssignmentType: {
    label: 'Role Assignment Type',
    apiMethod: 'listRoleAssignmentTypes',
    slug: 'role_assignment_types',
  },
  organizationalUnitMember: {
    label: 'Organizational Unit Member',
    apiMethod: 'listOrganizationalUnitMembers',
    slug: 'organizational_unit_members',
  },
  channel: {
    label: 'Channel',
    apiMethod: 'listChannels',
    slug: 'channels',
  },
  channelAccount: {
    label: 'Channel Account',
    apiMethod: 'listChannelAccounts',
    slug: 'channel_accounts',
  },
  complianceBaseline: {
    label: 'Compliance Baseline',
    apiMethod: 'listComplianceBaselines',
    slug: 'compliance_baselines',
  },
  contact: {
    label: 'Contact',
    apiMethod: 'listContacts',
    slug: 'contacts',
  },
  device: { label: 'Device', apiMethod: 'listDevices', slug: 'devices' },
  deviceAccount: {
    label: 'Device Account',
    apiMethod: 'listDeviceAccounts',
    slug: 'device_accounts',
  },
  deviceApplication: {
    label: 'Device Application',
    apiMethod: 'listDeviceApplications',
    slug: 'device_applications',
  },
  employee: {
    label: 'Employee',
    apiMethod: 'listEmployees',
    slug: 'employees',
  },
  group: { label: 'Group', apiMethod: 'listGroups', slug: 'groups' },
  groupLicense: {
    label: 'Group License',
    apiMethod: 'listGroupLicenses',
    slug: 'group_licenses',
  },
  groupMember: {
    label: 'Group Member',
    apiMethod: 'listGroupMembers',
    slug: 'group_members',
  },
  groupPermission: {
    label: 'Group Permission',
    apiMethod: 'listGroupPermissions',
    slug: 'group_permissions',
  },
  groupRole: {
    label: 'Group Role',
    apiMethod: 'listGroupRoles',
    slug: 'group_roles',
  },
  license: { label: 'License', apiMethod: 'listLicenses', slug: 'licenses' },
  location: {
    label: 'Location',
    apiMethod: 'listLocations',
    slug: 'locations',
  },
  multiFactor: {
    label: 'Multi-Factor',
    apiMethod: 'listMultiFactors',
    slug: 'multi_factors',
  },
  networkObject: {
    label: 'Network Object',
    apiMethod: 'listNetworkObjects',
    slug: 'network_objects',
  },
  organizationalUnit: {
    label: 'Organizational Unit',
    apiMethod: 'listOrganizationalUnits',
    slug: 'organizational_units',
  },
  passwordPolicy: {
    label: 'Password Policy',
    apiMethod: 'listPasswordPolicies',
    slug: 'password_policies',
  },
  permission: {
    label: 'Permission',
    apiMethod: 'listPermissions',
    slug: 'permissions',
  },
  resource: {
    label: 'Resource',
    apiMethod: 'listResources',
    slug: 'resources',
  },
  federationService: {
    label: 'Federation Service',
    apiMethod: 'listFederationServices',
    slug: 'federation_services',
  },
  federationServiceProvider: {
    label: 'Federation Service Provider',
    apiMethod: 'listFederationServiceProviders',
    slug: 'federation_service_providers',
  },
  person: { label: 'Person', apiMethod: 'listPersons', slug: 'persons' },
  policy: { label: 'Policy', apiMethod: 'listPolicies', slug: 'policies' },
  role: { label: 'Role', apiMethod: 'listRoles', slug: 'roles' },
  rolePermission: {
    label: 'Role Permission',
    apiMethod: 'listRolePermissions',
    slug: 'role_permissions',
  },
  resourcePermission: {
    label: 'Resource Permission',
    apiMethod: 'listResourcePermissions',
    slug: 'resource_permissions',
  },
  secret: {
    label: 'Secret',
    apiMethod: 'listSecrets',
    slug: 'secrets',
  },
  secretResource: {
    label: 'Secret Resource',
    apiMethod: 'listSecretResources',
    slug: 'secret_resources',
  },
  securityConfiguration: {
    label: 'Security Configuration',
    apiMethod: 'listSecurityConfigurations',
    slug: 'security_configurations',
  },
  specialPrincipal: {
    label: 'Special Principal',
    apiMethod: 'listSpecialPrincipals',
    slug: 'special_principals',
  },
  ticket: {
    label: 'Ticket',
    apiMethod: 'listTickets',
    slug: 'tickets',
  },
  ticketComment: {
    label: 'Ticket Comment',
    apiMethod: 'listTicketComments',
    slug: 'ticket_comments',
  },
} as const satisfies Record<
  CatalogEntityId,
  {
    label: string;
    apiMethod: Extract<keyof typeof api, `list${string}`>;
    slug: string;
  }
>;

export type CatalogListApiMethod =
  (typeof CATALOG_ENTITY_DEFINITIONS)[CatalogEntityId]['apiMethod'];

const CATALOG_ENTITY_EXTRACTORS: {
  [K in CatalogEntityId]: (model: RawEntityModel) => CatalogEntityTypeMap[K];
} = {
  account: (model) =>
    extractAccount(model as Parameters<typeof extractAccount>[0]),
  person: (model) =>
    extractPerson(model as Parameters<typeof extractPerson>[0]),
  group: (model) => extractGroup(model as Parameters<typeof extractGroup>[0]),
  role: (model) => extractRole(model as Parameters<typeof extractRole>[0]),
  license: (model) =>
    extractLicense(model as Parameters<typeof extractLicense>[0]),
  permission: (model) =>
    extractPermission(model as Parameters<typeof extractPermission>[0]),
  resource: (model) =>
    extractResource(model as Parameters<typeof extractResource>[0]),
  federationService: (model) =>
    extractFederationService(
      model as Parameters<typeof extractFederationService>[0]
    ),
  federationServiceProvider: (model) =>
    extractFederationServiceProvider(
      model as Parameters<typeof extractFederationServiceProvider>[0]
    ),
  application: (model) =>
    extractApplication(model as Parameters<typeof extractApplication>[0]),
  attributes: (model) =>
    extractAttributes(model as Parameters<typeof extractAttributes>[0]),
  classification: (model) =>
    extractClassification(model as Parameters<typeof extractClassification>[0]),
  riskFactor: (model) =>
    extractRiskFactor(model as Parameters<typeof extractRiskFactor>[0]),
  accountAttribute: (model) =>
    extractAccountAttribute(
      model as Parameters<typeof extractAccountAttribute>[0]
    ),
  accountClassification: (model) =>
    extractAccountClassification(
      model as Parameters<typeof extractAccountClassification>[0]
    ),
  accountRiskFactor: (model) =>
    extractAccountRiskFactor(
      model as Parameters<typeof extractAccountRiskFactor>[0]
    ),
  accountExtension: (model) =>
    extractAccountExtension(
      model as Parameters<typeof extractAccountExtension>[0]
    ),
  groupAttribute: (model) =>
    extractGroupAttribute(model as Parameters<typeof extractGroupAttribute>[0]),
  groupExtension: (model) =>
    extractGroupExtension(model as Parameters<typeof extractGroupExtension>[0]),
  personAccount: (model) =>
    extractPersonAccount(model as Parameters<typeof extractPersonAccount>[0]),
  personAttribute: (model) =>
    extractPersonAttribute(
      model as Parameters<typeof extractPersonAttribute>[0]
    ),
  peopleExtension: (model) =>
    extractPeopleExtension(
      model as Parameters<typeof extractPeopleExtension>[0]
    ),
  resourceContainer: (model) =>
    extractResourceContainer(
      model as Parameters<typeof extractResourceContainer>[0]
    ),
  resourceContainerResource: (model) =>
    extractResourceContainerResource(
      model as Parameters<typeof extractResourceContainerResource>[0]
    ),
  resourceContainerResourceContainer: (model) =>
    extractResourceContainerResourceContainer(
      model as Parameters<typeof extractResourceContainerResourceContainer>[0]
    ),
  scopedAssignment: (model) =>
    extractScopedAssignment(
      model as Parameters<typeof extractScopedAssignment>[0]
    ),
  scopedDenial: (model) =>
    extractScopedDenial(model as Parameters<typeof extractScopedDenial>[0]),
  serviceAccountImpersonation: (model) =>
    extractServiceAccountImpersonation(
      model as Parameters<typeof extractServiceAccountImpersonation>[0]
    ),
  groupMembershipRole: (model) =>
    extractGroupMembershipRole(
      model as Parameters<typeof extractGroupMembershipRole>[0]
    ),
  roleAssignmentType: (model) =>
    extractRoleAssignmentType(
      model as Parameters<typeof extractRoleAssignmentType>[0]
    ),
  organizationalUnitMember: (model) =>
    extractOrganizationalUnitMember(
      model as Parameters<typeof extractOrganizationalUnitMember>[0]
    ),
  channel: (model) =>
    extractChannel(model as Parameters<typeof extractChannel>[0]),
  channelAccount: (model) =>
    extractChannelAccount(model as Parameters<typeof extractChannelAccount>[0]),
  complianceBaseline: (model) =>
    extractComplianceBaseline(
      model as Parameters<typeof extractComplianceBaseline>[0]
    ),
  contact: (model) =>
    extractContact(model as Parameters<typeof extractContact>[0]),
  device: (model) =>
    extractDevice(model as Parameters<typeof extractDevice>[0]),
  employee: (model) =>
    extractEmployee(model as Parameters<typeof extractEmployee>[0]),
  location: (model) =>
    extractLocation(model as Parameters<typeof extractLocation>[0]),
  policy: (model) =>
    extractPolicy(model as Parameters<typeof extractPolicy>[0]),
  multiFactor: (model) =>
    extractMultiFactor(model as Parameters<typeof extractMultiFactor>[0]),
  networkObject: (model) =>
    extractNetworkObject(model as Parameters<typeof extractNetworkObject>[0]),
  organizationalUnit: (model) =>
    extractOrganizationalUnit(
      model as Parameters<typeof extractOrganizationalUnit>[0]
    ),
  passwordPolicy: (model) =>
    extractPasswordPolicy(model as Parameters<typeof extractPasswordPolicy>[0]),
  secret: (model) =>
    extractSecret(model as Parameters<typeof extractSecret>[0]),
  secretResource: (model) =>
    extractSecretResource(model as Parameters<typeof extractSecretResource>[0]),
  securityConfiguration: (model) =>
    extractSecurityConfiguration(
      model as Parameters<typeof extractSecurityConfiguration>[0]
    ),
  specialPrincipal: (model) =>
    extractSpecialPrincipal(
      model as Parameters<typeof extractSpecialPrincipal>[0]
    ),
  accountLicense: (model) =>
    extractAccountLicense(model as Parameters<typeof extractAccountLicense>[0]),
  accountManager: (model) =>
    extractAccountManager(model as Parameters<typeof extractAccountManager>[0]),
  accountMultiFactor: (model) =>
    extractAccountMultiFactor(
      model as Parameters<typeof extractAccountMultiFactor>[0]
    ),
  accountSecret: (model) =>
    extractAccountSecret(model as Parameters<typeof extractAccountSecret>[0]),
  accountPermission: (model) =>
    extractAccountPermission(
      model as Parameters<typeof extractAccountPermission>[0]
    ),
  accountPolicy: (model) =>
    extractAccountPolicy(model as Parameters<typeof extractAccountPolicy>[0]),
  accountRole: (model) =>
    extractAccountRole(model as Parameters<typeof extractAccountRole>[0]),
  resourceExtension: (model) =>
    extractResourceExtension(
      model as Parameters<typeof extractResourceExtension>[0]
    ),
  roleExtension: (model) =>
    extractRoleExtension(model as Parameters<typeof extractRoleExtension>[0]),
  agent: (model) => extractAgent(model as Parameters<typeof extractAgent>[0]),
  agentAccount: (model) =>
    extractAgentAccount(model as Parameters<typeof extractAgentAccount>[0]),
  agentCapability: (model) =>
    extractAgentCapability(
      model as Parameters<typeof extractAgentCapability>[0]
    ),
  agentDeployment: (model) =>
    extractAgentDeployment(
      model as Parameters<typeof extractAgentDeployment>[0]
    ),
  agentDeploymentAttribute: (model) =>
    extractAgentDeploymentAttribute(
      model as Parameters<typeof extractAgentDeploymentAttribute>[0]
    ),
  agentDeploymentExtension: (model) =>
    extractAgentDeploymentExtension(
      model as Parameters<typeof extractAgentDeploymentExtension>[0]
    ),
  agentDeploymentResource: (model) =>
    extractAgentDeploymentResource(
      model as Parameters<typeof extractAgentDeploymentResource>[0]
    ),
  agentOwner: (model) =>
    extractAgentOwner(model as Parameters<typeof extractAgentOwner>[0]),
  applicationAgent: (model) =>
    extractApplicationAgent(
      model as Parameters<typeof extractApplicationAgent>[0]
    ),
  applicationAccount: (model) =>
    extractApplicationAccount(
      model as Parameters<typeof extractApplicationAccount>[0]
    ),
  applicationGroup: (model) =>
    extractApplicationGroup(
      model as Parameters<typeof extractApplicationGroup>[0]
    ),
  applicationPermission: (model) =>
    extractApplicationPermission(
      model as Parameters<typeof extractApplicationPermission>[0]
    ),
  applicationRole: (model) =>
    extractApplicationRole(
      model as Parameters<typeof extractApplicationRole>[0]
    ),
  deviceAccount: (model) =>
    extractDeviceAccount(model as Parameters<typeof extractDeviceAccount>[0]),
  deviceApplication: (model) =>
    extractDeviceApplication(
      model as Parameters<typeof extractDeviceApplication>[0]
    ),
  groupLicense: (model) =>
    extractGroupLicense(model as Parameters<typeof extractGroupLicense>[0]),
  groupMember: (model) =>
    extractGroupMember(model as Parameters<typeof extractGroupMember>[0]),
  groupPermission: (model) =>
    extractGroupPermission(
      model as Parameters<typeof extractGroupPermission>[0]
    ),
  groupRole: (model) =>
    extractGroupRole(model as Parameters<typeof extractGroupRole>[0]),
  rolePermission: (model) =>
    extractRolePermission(model as Parameters<typeof extractRolePermission>[0]),
  resourcePermission: (model) =>
    extractResourcePermission(
      model as Parameters<typeof extractResourcePermission>[0]
    ),
  ticket: (model) =>
    extractTicket(model as Parameters<typeof extractTicket>[0]),
  ticketComment: (model) =>
    extractTicketComment(model as Parameters<typeof extractTicketComment>[0]),
};

type CatalogEntityDefinition =
  (typeof CATALOG_ENTITY_DEFINITIONS)[CatalogEntityId];

export type CatalogEntityRegistryEntry<K extends CatalogEntityId> =
  CatalogEntityDefinition & {
    id: K;
    extractor: (model: RawEntityModel) => CatalogEntityTypeMap[K];
  };

export const CATALOG_ENTITY_REGISTRY = Object.fromEntries(
  (
    Object.entries(CATALOG_ENTITY_DEFINITIONS) as Array<
      [CatalogEntityId, CatalogEntityDefinition]
    >
  ).map(([entityId, definition]) => {
    return [
      entityId,
      {
        id: entityId,
        ...definition,
        extractor: CATALOG_ENTITY_EXTRACTORS[entityId],
      },
    ] as const;
  })
) as {
  [K in CatalogEntityId]: CatalogEntityRegistryEntry<K>;
};

function toSlugKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function toCompactSlugKey(value: string): string {
  return value.replace(/[^a-z0-9]/g, '');
}

export const CATALOG_ENTITY_BY_SLUG: Readonly<Record<string, CatalogEntityId>> =
  Object.fromEntries(
    (
      Object.entries(CATALOG_ENTITY_REGISTRY) as Array<
        [CatalogEntityId, CatalogEntityRegistryEntry<CatalogEntityId>]
      >
    ).flatMap(([entityId, entry]) => {
      const slugKey = toSlugKey(entry.slug);
      const compactSlugKey = toCompactSlugKey(slugKey);

      return [
        [slugKey, entityId],
        [compactSlugKey, entityId],
      ] as const;
    })
  );

export function resolveCatalogEntityId(
  value: string | null | undefined
): CatalogEntityId | undefined {
  if (!value) {
    return undefined;
  }

  if (value in CATALOG_ENTITY_REGISTRY) {
    return value as CatalogEntityId;
  }

  const slugKey = toSlugKey(value);
  if (slugKey in CATALOG_ENTITY_BY_SLUG) {
    return CATALOG_ENTITY_BY_SLUG[slugKey];
  }

  const compactSlugKey = toCompactSlugKey(slugKey);
  return CATALOG_ENTITY_BY_SLUG[compactSlugKey];
}

export function resolveCatalogEntityLabel(slug: string): string {
  const entityId = resolveCatalogEntityId(slug);
  return entityId ? CATALOG_ENTITY_DEFINITIONS[entityId].label : slug;
}

export function invokeCatalogListApi(
  apiMethod: CatalogListApiMethod,
  tenantId: UUID,
  connectorId: UUID,
  query?: PageRequest
) {
  const method = api[apiMethod] as CatalogListApiFn;
  const apiQuery = toApiQuery(query);
  return method(tenantId, connectorId, apiQuery);
}

export function extractCatalogEntityModels<K extends CatalogEntityId>(
  entityId: K,
  rawModels: RawEntityModel[]
): CatalogEntityTypeMap[K][] {
  const extractor = CATALOG_ENTITY_REGISTRY[entityId].extractor;
  return rawModels.map((model) => extractor(model));
}
